import React from 'react';
import { ExternalLink, FileVideo, Image as ImageIcon, Layers } from 'lucide-react';
import GuionTableEditor from './GuionTableEditor';

export const CREATIVE_TIPOS = { imagen: 'Imagen', video: 'Vídeo', carrusel: 'Carrusel' };
const TIPO_ICON = { imagen: ImageIcon, video: FileVideo, carrusel: Layers };
export const CREATIVE_ESTADOS = {
  borrador: ['Borrador', 'bg-slate-100 text-slate-700'],
  pendiente: ['Pendiente de aprobar', 'bg-amber-100 text-amber-800'],
  aprobado: ['Aprobado', 'bg-emerald-100 text-emerald-800'],
  cambios: ['Cambios solicitados', 'bg-red-100 text-red-700'],
};
export const BATCH_ESTADOS = {
  borrador: ['Borrador (no enviada)', 'bg-slate-100 text-slate-700'],
  pendiente: ['Pendiente', 'bg-amber-100 text-amber-800'],
  aprobada: ['Aprobada', 'bg-emerald-100 text-emerald-800'],
  con_cambios: ['Con cambios', 'bg-red-100 text-red-700'],
};
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtDateTime = (iso) => new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export const Badge = ({ map, value, testId }) => {
  const [label, cls] = map[value] || [value, 'bg-muted'];
  return <span className={`text-xs font-medium rounded-full px-2.5 py-0.5 whitespace-nowrap ${cls}`} data-testid={testId}>{label}</span>;
};

const driveFileId = (url) => (url || '').match(/\/file\/d\/([\w-]+)/)?.[1] || (url || '').match(/[?&]id=([\w-]+)/)?.[1];
const driveFolderId = (url) => (url || '').match(/\/folders\/([\w-]+)/)?.[1];
export const canPreview = (url) => !!(driveFileId(url) || driveFolderId(url));

export const DrivePreview = ({ url, testId }) => {
  if (!url) return <p className="text-sm text-muted-foreground italic">Sin archivo enlazado todavía.</p>;
  const id = driveFileId(url);
  const folder = !id && driveFolderId(url);
  return (
    <div className="space-y-2" data-testid={testId}>
      {id && <iframe title="Vista previa" src={`https://drive.google.com/file/d/${id}/preview`} className="w-full aspect-video rounded-lg border bg-muted" allow="autoplay" />}
      {folder && <iframe title="Archivos del anuncio" src={`https://drive.google.com/embeddedfolderview?id=${folder}#grid`} className="w-full h-[420px] rounded-lg border bg-white" data-testid={`${testId}-folder`} />}
      <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid={`${testId}-link`}>
        <ExternalLink className="w-3.5 h-3.5" />Abrir en Drive
      </a>
    </div>
  );
};

export const ObservationsList = ({ items }) => (items?.length ? (
  <div className="space-y-1.5" data-testid="creative-observations">
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Historial de observaciones</p>
    {items.map((o) => (
      <div key={o.id} className={`rounded-lg px-3 py-2 text-sm ${o.autor_tipo === 'cliente' ? 'bg-red-50 border border-red-100' : 'bg-muted/60'}`}>
        <p className="text-[11px] text-muted-foreground">{o.autor_nombre} · {fmtDateTime(o.created_at)}</p>
        <p className="whitespace-pre-wrap">{o.texto}</p>
      </div>
    ))}
  </div>
) : null);

export const CreativeBody = ({ c, idx }) => {
  const Icon = TIPO_ICON[c.tipo] || ImageIcon;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className="w-4 h-4 text-primary" />
        <span className="font-semibold">{c.nombre}</span>
        <span className="text-xs text-muted-foreground">{CREATIVE_TIPOS[c.tipo]}</span>
        <span className="ml-auto"><Badge map={CREATIVE_ESTADOS} value={c.estado} testId={`creative-status-${idx}`} /></span>
      </div>
      <DrivePreview url={c.drive_url} testId={`creative-drive-${idx}`} />
      {c.copy_text && (
        <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Copy del anuncio</p>
          <p className="text-sm whitespace-pre-wrap rounded-lg bg-muted/40 p-3" data-testid={`creative-copy-${idx}`}>{c.copy_text}</p></div>
      )}
      {c.tipo === 'video' && c.guion_table?.length > 0 && (
        <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Guion</p>
          <GuionTableEditor rows={c.guion_table} readOnly /></div>
      )}
    </div>
  );
};
